import { useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { AlertTriangle, Download } from 'lucide-react';
import type { HansAckerReport, HansAckerScope, HansAckerSeverity } from './types';
import PostureSummary from './PostureSummary';
import FixFirst from './FixFirst';
import FindingsTable from './FindingsTable';
import FindingDrawer from './FindingDrawer';
import CoverageSection from './CoverageSection';
import Recommendations from './Recommendations';
import PreviousScans from './PreviousScans';
import { filterFindings } from './utils';

const SCOPES: { id: HansAckerScope; label: string }[] = [
  { id: 'all', label: 'Whole repo' },
  { id: 'backend', label: 'backend' },
  { id: 'frontend', label: 'frontend' },
];

const SECTIONS = [
  { id: 'hans-acker-summary', label: 'Summary' },
  { id: 'hans-acker-findings', label: 'Findings' },
  { id: 'hans-acker-coverage', label: 'Coverage' },
  { id: 'hans-acker-roadmap', label: 'Recommendations' },
];

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export default function HansAckerTab({ report }: { report: HansAckerReport }) {
  const theme = useTheme();
  const [scope, setScope] = useState<HansAckerScope>('all');
  const [severity, setSeverity] = useState<HansAckerSeverity | 'all'>('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const scoped = useMemo(() => filterFindings(report.findings, scope, 'all'), [report.findings, scope]);
  const openFinding = report.findings.find((f) => f.id === openId) ?? null;
  const sections = report.scans?.length ? [...SECTIONS, { id: 'hans-acker-scans', label: 'Previous scans' }] : SECTIONS;

  const selectSeverity = (s: HansAckerSeverity | 'all') => { setSeverity(s); setPage(1); };

  return (
    <Stack spacing={2.5} sx={{ mt: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: 'text.disabled', mr: 0.5 }}>Scope</Typography>
        {SCOPES.map((s) => {
          const n = filterFindings(report.findings, s.id, 'all').length;
          const active = scope === s.id;
          return (
            <Chip
              key={s.id}
              clickable
              onClick={() => { setScope(s.id); setPage(1); }}
              label={<>{s.label}<Box component="small" sx={{ ml: 0.75, opacity: 0.7 }}>{n}</Box></>}
              sx={{ fontWeight: 600, bgcolor: active ? alpha('#ef4444', 0.14) : alpha(theme.palette.text.primary, 0.05), color: active ? '#ef4444' : 'text.secondary', border: '1px solid', borderColor: active ? alpha('#ef4444', 0.4) : 'divider' }}
            />
          );
        })}
        <Typography sx={{ fontSize: 11, color: 'text.disabled', ml: 1 }}>Latest scan covering each folder is shown</Typography>
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
        {sections.map((s) => (
          <Button key={s.id} size="small" onClick={() => scrollTo(s.id)} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderRadius: 4 }}>{s.label}</Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" disableElevation startIcon={<Download size={14} />} component="a" href={report.downloads.zip} download sx={{ textTransform: 'none' }}>Download report</Button>
      </Stack>

      <PostureSummary report={report} scopedFindings={scoped} onSelectSeverity={(s) => { selectSeverity(s); scrollTo('hans-acker-findings'); }} />

      <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1, px: 2, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', fontSize: 12.5, color: 'text.secondary' }}>
        {[['Scanned', report.meta.scanned], ['Duration', report.meta.duration], ['Mode', report.meta.mode], ['Surfaces reviewed', String(report.meta.surfaces)]].map(([k, v]) => (
          <Box key={k} component="span">{k} <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>{v}</Box></Box>
        ))}
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.25, py: 0.4, borderRadius: 1.5, fontWeight: 600, color: '#d97706', bgcolor: alpha('#f59e0b', 0.12), border: '1px solid', borderColor: alpha('#f59e0b', 0.3) }}>
          <AlertTriangle size={13} /><span>{report.meta.note ?? 'Static analysis \u2014 runtime not validated'}</span>
        </Stack>
      </Stack>

      {report.fixFirst.length > 0 && <FixFirst steps={report.fixFirst} findings={report.findings} onOpen={setOpenId} />}
      <FindingsTable findings={scoped} severity={severity} page={page} onSeverityChange={selectSeverity} onPageChange={setPage} onOpen={setOpenId} />
      <CoverageSection coverage={report.coverage} />
      <Recommendations phases={report.recommendations} />
      {report.scans?.length ? <PreviousScans scans={report.scans} /> : null}

      <FindingDrawer finding={openFinding} onClose={() => setOpenId(null)} />
    </Stack>
  );
}
